import { useQuery, UseQueryResult } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useState } from "react"
import type { TablePaginationConfig } from "antd/es/table"
import { AvailableAxiosInstances, instanceMap } from "utils/request"
import { onQueryError } from "utils/utils"
import { useSearchParams } from "react-router-dom"

type QueryPrimitive = string | number | boolean | null | undefined
type QueryParamValue = string | number | boolean | null | undefined

type UseBackendTableQueryOptions<TData> = {
  queryKeyBase: QueryPrimitive[]
  // Provide either `url` (raw axios fetch) or `queryFn` (service-layer fetch).
  url?: string
  queryFn?: (params: Record<string, QueryParamValue>) => Promise<TData>
  getTotal: (data?: TData) => number
  instanceKey?: AvailableAxiosInstances
  enabled?: boolean
  keepPreviousData?: boolean
  staleTime?: number
  initialPageNumber?: number
  initialPageSize?: number
  initialSearch?: string
  searchDebounceMs?: number
  pageNumberParamName?: string
  pageSizeParamName?: string
  searchParamName?: string
  pageSizeOptions?: number[]
  initialExtraParams?: Record<string, QueryParamValue>
  onSuccess?: (data: TData) => void
}

type UseBackendTableQueryResult<TData> = UseQueryResult<TData> & {
  pageNumber: number
  pageSize: number
  search: string
  debouncedSearch: string
  isSearching: boolean
  queryParams: Record<string, QueryParamValue>
  extraParams: Record<string, QueryParamValue>
  tablePagination: TablePaginationConfig
  handleSearchChange: (value: string) => void
  handlePaginationChange: (page: number, size?: number) => void
  setQueryExtraParams: (params: Record<string, QueryParamValue>) => void
  patchQueryExtraParams: (params: Record<string, QueryParamValue>) => void
}

export function useBackendTableQuery<TData>({
  queryKeyBase,
  url,
  queryFn: fetcher,
  getTotal,
  instanceKey,
  enabled = true,
  keepPreviousData = true,
  staleTime,
  initialPageNumber = 1,
  initialPageSize = 20,
  initialSearch = "",
  searchDebounceMs = 500,
  pageNumberParamName = "pageNumber",
  pageSizeParamName = "pageSize",
  searchParamName = "searchFilter",
  pageSizeOptions = [20, 40, 100, 500, 1000],
  initialExtraParams = {},
  onSuccess,
}: UseBackendTableQueryOptions<TData>): UseBackendTableQueryResult<TData> {
  const [pageNumber, setPageNumber] = useState(initialPageNumber)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [searchParams, setSearchParams] = useSearchParams()
  const search = searchParams.get(searchParamName) || initialSearch
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch)
  const [extraParams, setExtraParams] =
    useState<Record<string, QueryParamValue>>(initialExtraParams)

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search)
    }, searchDebounceMs)

    return () => {
      window.clearTimeout(timer)
    }
  }, [search, searchDebounceMs])

  const queryParams = useMemo(
    () => ({
      ...extraParams,
      [pageNumberParamName]: pageNumber,
      [pageSizeParamName]: pageSize,
      [searchParamName]: debouncedSearch,
    }),
    [
      extraParams,
      pageNumberParamName,
      pageNumber,
      pageSizeParamName,
      pageSize,
      searchParamName,
      debouncedSearch,
    ]
  )

  const query = useQuery<TData>({
    queryKey: [...queryKeyBase, queryParams],
    queryFn: async () => {
      if (fetcher) {
        return fetcher(queryParams)
      }
      if (!url) {
        throw new Error(
          "useBackendTableQuery requires either `url` or `queryFn`"
        )
      }
      if (!instanceKey) {
        throw new Error(
          "useBackendTableQuery requires `instanceKey` alongside `url`"
        )
      }
      const axiosInstanceToUse = instanceMap[instanceKey]
      const res = await axiosInstanceToUse.get(url, {
        params: queryParams,
      })
      return res.data
    },
    enabled,
    keepPreviousData,
    staleTime,
    onError: onQueryError,
    onSuccess: onSuccess,
  })

  const handleSearchChange = (value: string) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev.toString())
        if (value) {
          newParams.set(searchParamName, value)
        } else {
          newParams.delete(searchParamName)
        }
        return newParams
      },
      // Typing is not navigation: replace the current entry instead of pushing
      // one per keystroke, so Back leaves the page rather than retyping the
      // search term backwards one character at a time.
      { replace: true }
    )
    setPageNumber(1)
  }

  const handlePaginationChange = useCallback(
    (page: number, size?: number) => {
      if (typeof size === "number" && size !== pageSize) {
        setPageSize(size)
      }
      setPageNumber(page)
    },
    [pageSize]
  )

  const setQueryExtraParams = (params: Record<string, QueryParamValue>) => {
    setExtraParams(params)
    setPageNumber(1)
  }

  const patchQueryExtraParams = (params: Record<string, QueryParamValue>) => {
    setExtraParams((prev) => ({
      ...prev,
      ...params,
    }))
    setPageNumber(1)
  }

  const tablePagination = useMemo(
    () => ({
      current: pageNumber,
      pageSize,
      total: getTotal(query.data),
      onChange: handlePaginationChange,
      pageSizeOptions,
      showSizeChanger: true,
      showTotal: (count: number, range: [number, number]) =>
        `${range?.[0]}-${range?.[1]} of ${count} items`,
    }),
    [pageNumber, pageSize, getTotal, query.data, pageSizeOptions]
  )

  const isSearching =
    search !== debouncedSearch || (query.isFetching && query.isPreviousData)

  return {
    ...query,
    pageNumber,
    pageSize,
    search,
    debouncedSearch,
    isSearching,
    queryParams,
    extraParams,
    tablePagination,
    handleSearchChange,
    handlePaginationChange,
    setQueryExtraParams,
    patchQueryExtraParams,
  }
}
